/**
 * Genesis Challenge Hooks
 *
 * React hooks for Genesis Wizard Spark integration.
 * Handles key holder registration and proposal signing via Spark.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useEstreamClient } from './react.js';
import { SPARK_TOPICS, GENESIS_ACTIONS, createGenesisKeyHolderChallenge, createGenesisProposalChallenge, } from './spark-liveness.js';
// ============================================================================
// Key Holder Registration Hook
// ============================================================================
/**
 * Hook for registering genesis key holders via Spark
 *
 * @example
 * ```tsx
 * const {
 *   state,
 *   startChallenge,
 *   challengeJson,
 *   timeRemaining
 * } = useGenesisKeyHolder({
 *   networkName: 'alpha-devnet',
 *   onRegistered: (result) => {
 *     addKeyHolder({
 *       id: result.walletId,
 *       publicKey: result.publicKey,
 *       name: result.displayName,
 *     });
 *   },
 * });
 *
 * // Start challenge
 * await startChallenge(1, 'Founder');
 *
 * // Render Spark
 * {challengeJson && <SparkRenderer challengeJson={challengeJson} variant="genesis" />}
 * ```
 */
export function useGenesisKeyHolder(options) {
    const { networkName, edgeUrl = typeof window !== 'undefined' ? window.location.origin : '', onRegistered, onError, timeout = 300_000, enablePolling = true, pollingInterval = 2000, } = options;
    const client = useEstreamClient();
    const [state, setState] = useState({ status: 'idle' });
    const [timeRemaining, setTimeRemaining] = useState(null);
    const timerRef = useRef(null);
    const pollingRef = useRef(null);
    const subscriptionRef = useRef(null);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current)
                clearInterval(timerRef.current);
            if (pollingRef.current)
                clearInterval(pollingRef.current);
            if (subscriptionRef.current)
                subscriptionRef.current();
        };
    }, []);
    // Handle successful registration
    const handleRegistered = useCallback((response) => {
        console.log('[Genesis] Key holder registered:', response.wallet_id.slice(0, 16));
        const result = {
            walletId: response.wallet_id,
            publicKey: response.public_key,
            displayName: response.display_name,
            registeredAt: response.timestamp,
        };
        setState({ status: 'completed', result });
        // Cleanup
        if (pollingRef.current)
            clearInterval(pollingRef.current);
        if (subscriptionRef.current)
            subscriptionRef.current();
        onRegistered?.(result);
    }, [onRegistered]);
    // Poll for key holder response (HTTP backup)
    const pollKeyHolderStatus = useCallback(async (sessionId) => {
        if (!enablePolling)
            return;
        try {
            const response = await fetch(`${edgeUrl}/api/genesis/key-holder-status/${sessionId}`);
            if (!response.ok)
                return;
            const data = await response.json();
            console.debug('[Genesis] Poll response:', { status: data.status });
            if (data.status === 'registered' && data.result) {
                handleRegistered(data.result);
            }
        }
        catch (e) {
            console.debug('[Genesis] Polling error:', e);
        }
    }, [edgeUrl, enablePolling, handleRegistered]);
    // Countdown timer
    useEffect(() => {
        if (state.status === 'pending') {
            const challenge = state.challenge;
            const updateTimer = () => {
                const remaining = Math.max(0, Math.floor((challenge.expiresAt - Date.now()) / 1000));
                setTimeRemaining(remaining);
                if (remaining <= 0) {
                    setState({ status: 'expired', challenge });
                    if (timerRef.current)
                        clearInterval(timerRef.current);
                    if (pollingRef.current)
                        clearInterval(pollingRef.current);
                }
            };
            updateTimer();
            timerRef.current = setInterval(updateTimer, 1000);
            return () => {
                if (timerRef.current)
                    clearInterval(timerRef.current);
            };
        }
        else {
            setTimeRemaining(null);
        }
    }, [state]);
    const startChallenge = useCallback(async (keyHolderIndex, keyHolderName) => {
        try {
            // Create challenge using SDK utility
            const { payload, envelope } = createGenesisKeyHolderChallenge({
                networkName,
                keyHolderIndex,
                keyHolderName,
                expiresIn: timeout,
            });
            const challenge = {
                challengeId: envelope.challenge_id,
                sessionId: payload.session_id,
                displayCode: envelope.challenge_id.slice(0, 8).toUpperCase(),
                expiresAt: payload.expires_at,
                payload,
            };
            setState({ status: 'pending', challenge });
            // Post challenge to edge API for mobile app discovery
            try {
                const apiChallenge = {
                    challenge_id: envelope.challenge_id,
                    session_id: payload.session_id,
                    display_code: envelope.challenge_id.slice(0, 8).toUpperCase(),
                    expires_at: payload.expires_at,
                    created_at: Date.now(),
                    payload: envelope.payload,
                    action: GENESIS_ACTIONS.ADD_KEY_HOLDER,
                    auth_level: 3, // BiometricPinDelay for governance
                    bound_action: {
                        action_type: 'genesis_key_holder',
                        action_hash: envelope.challenge_id,
                        description: `Add governance key holder #${keyHolderIndex}: ${keyHolderName}`,
                    },
                };
                await fetch(`${edgeUrl}/api/genesis/key-holder-challenge`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'add_key_holder',
                        keyHolderIndex,
                        keyHolderName,
                        challengeData: apiChallenge,
                    }),
                });
                console.debug('[Genesis] Posted key holder challenge to API:', {
                    challengeId: envelope.challenge_id,
                    sessionId: payload.session_id,
                });
            }
            catch (apiError) {
                console.warn('[Genesis] Failed to post to API, trying lattice:', apiError);
                // Fallback: try lattice
                try {
                    await client.emit(SPARK_TOPICS.PAYLOAD(envelope.challenge_id), envelope);
                    console.debug('[Genesis] Emitted payload envelope to lattice');
                }
                catch (latticeError) {
                    console.warn('[Genesis] Lattice emit also failed:', latticeError);
                }
            }
            // Subscribe to key holder responses
            const responseTopic = `/genesis/key-holder/${payload.session_id}`;
            const unsubscribe = client.subscribe(responseTopic, {
                onData: (response) => {
                    console.debug('[Genesis] Received key holder response:', response);
                    // Verify the response matches our challenge
                    if (response.challenge_nonce !== payload.challenge_nonce) {
                        console.warn('[Genesis] Challenge nonce mismatch');
                        return;
                    }
                    handleRegistered(response);
                },
                onError: (error) => {
                    console.error('[Genesis] Subscription error:', error);
                },
            });
            subscriptionRef.current = unsubscribe;
            // Start polling as backup
            if (enablePolling) {
                pollingRef.current = setInterval(() => {
                    pollKeyHolderStatus(payload.session_id);
                }, pollingInterval);
            }
        }
        catch (e) {
            const error = e instanceof Error ? e.message : String(e);
            setState({ status: 'error', error });
            onError?.(error);
        }
    }, [client, networkName, edgeUrl, timeout, enablePolling, pollingInterval, pollKeyHolderStatus, handleRegistered, onError]);
    const cancel = useCallback(() => {
        if (timerRef.current)
            clearInterval(timerRef.current);
        if (pollingRef.current)
            clearInterval(pollingRef.current);
        if (subscriptionRef.current)
            subscriptionRef.current();
        setState({ status: 'idle' });
    }, []);
    const challenge = state.status === 'pending' || state.status === 'expired'
        ? state.challenge
        : null;
    // Create challenge JSON for SparkRenderer
    const challengeJson = challenge ? JSON.stringify({
        challenge_id: challenge.challengeId,
        session_id: challenge.sessionId,
        display_code: challenge.displayCode,
        expires_at: challenge.expiresAt,
        payload: btoa(JSON.stringify(challenge.payload)),
    }) : null;
    return {
        state,
        startChallenge,
        cancel,
        timeRemaining,
        challenge,
        challengeJson,
    };
}
/**
 * Hook for signing genesis proposals via Spark
 */
export function useGenesisProposal(options) {
    const { networkName, proposalId, configHash, edgeUrl = typeof window !== 'undefined' ? window.location.origin : '', onSigned, onError, timeout = 300_000, enablePolling = true, pollingInterval = 2000, } = options;
    const client = useEstreamClient();
    const [state, setState] = useState({ status: 'idle' });
    const [timeRemaining, setTimeRemaining] = useState(null);
    const timerRef = useRef(null);
    const pollingRef = useRef(null);
    const subscriptionRef = useRef(null);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current)
                clearInterval(timerRef.current);
            if (pollingRef.current)
                clearInterval(pollingRef.current);
            if (subscriptionRef.current)
                subscriptionRef.current();
        };
    }, []);
    // Handle successful signature
    const handleSigned = useCallback((response) => {
        console.log('[Genesis] Proposal signed by:', response.wallet_id.slice(0, 16));
        const result = {
            walletId: response.wallet_id,
            signature: response.signature,
            signedAt: response.timestamp,
            verified: true,
        };
        setState({ status: 'completed', result });
        // Cleanup
        if (pollingRef.current)
            clearInterval(pollingRef.current);
        if (subscriptionRef.current)
            subscriptionRef.current();
        onSigned?.(result);
    }, [onSigned]);
    // Poll for signature status
    const pollSignatureStatus = useCallback(async (sessionId) => {
        if (!enablePolling)
            return;
        try {
            const response = await fetch(`${edgeUrl}/api/genesis/proposal/${proposalId}/signatures`);
            if (!response.ok)
                return;
            const data = await response.json();
            // Check if any new signatures match our session
            // This is a simplified check - in production you'd track per-session
        }
        catch (e) {
            console.debug('[Genesis] Signature polling error:', e);
        }
    }, [edgeUrl, proposalId, enablePolling]);
    // Countdown timer
    useEffect(() => {
        if (state.status === 'pending') {
            const challenge = state.challenge;
            const updateTimer = () => {
                const remaining = Math.max(0, Math.floor((challenge.expiresAt - Date.now()) / 1000));
                setTimeRemaining(remaining);
                if (remaining <= 0) {
                    setState({ status: 'expired', challenge });
                    if (timerRef.current)
                        clearInterval(timerRef.current);
                    if (pollingRef.current)
                        clearInterval(pollingRef.current);
                }
            };
            updateTimer();
            timerRef.current = setInterval(updateTimer, 1000);
            return () => {
                if (timerRef.current)
                    clearInterval(timerRef.current);
            };
        }
        else {
            setTimeRemaining(null);
        }
    }, [state]);
    const startChallenge = useCallback(async (keyHolderWalletId) => {
        try {
            // Create challenge
            const { payload, envelope } = createGenesisProposalChallenge({
                networkName,
                proposalId,
                configHash,
                expiresIn: timeout,
            });
            const challenge = {
                challengeId: envelope.challenge_id,
                sessionId: payload.session_id,
                displayCode: envelope.challenge_id.slice(0, 8).toUpperCase(),
                expiresAt: payload.expires_at,
                payload,
            };
            setState({ status: 'pending', challenge });
            // Post to API
            try {
                await fetch(`${edgeUrl}/api/genesis/proposal/${proposalId}/sign-request`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        key_holder_wallet_id: keyHolderWalletId,
                        challenge_id: envelope.challenge_id,
                        session_id: payload.session_id,
                        payload: envelope.payload,
                    }),
                });
            }
            catch (e) {
                console.warn('[Genesis] Failed to post sign request:', e);
            }
            // Subscribe to signature responses
            const responseTopic = `/genesis/proposal/${proposalId}/signatures/${payload.session_id}`;
            const unsubscribe = client.subscribe(responseTopic, {
                onData: (response) => {
                    if (response.challenge_nonce !== payload.challenge_nonce) {
                        console.warn('[Genesis] Challenge nonce mismatch');
                        return;
                    }
                    handleSigned(response);
                },
                onError: (error) => {
                    console.error('[Genesis] Subscription error:', error);
                },
            });
            subscriptionRef.current = unsubscribe;
            // Start polling
            if (enablePolling) {
                pollingRef.current = setInterval(() => {
                    pollSignatureStatus(payload.session_id);
                }, pollingInterval);
            }
        }
        catch (e) {
            const error = e instanceof Error ? e.message : String(e);
            setState({ status: 'error', error });
            onError?.(error);
        }
    }, [client, networkName, proposalId, configHash, edgeUrl, timeout, enablePolling, pollingInterval, pollSignatureStatus, handleSigned, onError]);
    const cancel = useCallback(() => {
        if (timerRef.current)
            clearInterval(timerRef.current);
        if (pollingRef.current)
            clearInterval(pollingRef.current);
        if (subscriptionRef.current)
            subscriptionRef.current();
        setState({ status: 'idle' });
    }, []);
    const challenge = state.status === 'pending' || state.status === 'expired'
        ? state.challenge
        : null;
    const challengeJson = challenge ? JSON.stringify({
        challenge_id: challenge.challengeId,
        session_id: challenge.sessionId,
        display_code: challenge.displayCode,
        expires_at: challenge.expiresAt,
        payload: btoa(JSON.stringify(challenge.payload)),
    }) : null;
    return {
        state,
        startChallenge,
        cancel,
        timeRemaining,
        challenge,
        challengeJson,
    };
}
//# sourceMappingURL=genesis.js.map